import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="w-full flex items-center justify-between px-3 py-2 bg-duvi-surface border border-duvi-border rounded-md text-left transition-colors hover:bg-duvi-elevated focus:outline-none focus:border-duvi-accent"
          style={{ fontSize: "var(--text-sm)", minHeight: "38px" }}
        >
          <span className="flex-1 truncate">
            {selectedLabels.length === 0 ? (
              <span className="text-duvi-text-muted">{placeholder}</span>
            ) : selectedLabels.length <= 2 ? (
              <span className="text-duvi-text-primary">
                {selectedLabels.join(", ")}
              </span>
            ) : (
              <span className="text-duvi-text-primary">
                {selectedLabels.length} selected
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-duvi-text-secondary shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-duvi-surface border-duvi-border"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command className="bg-duvi-surface">
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-duvi-text-primary"
          />
          <CommandList>
            <CommandEmpty
              className="text-duvi-text-secondary py-3 text-center"
              style={{ fontSize: "var(--text-sm)" }}
            >
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggleOption(option.value)}
                    className="flex items-center gap-2 text-duvi-text-primary cursor-pointer"
                  >
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? "bg-duvi-accent border-duvi-accent"
                          : "border-duvi-border"
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span style={{ fontSize: "var(--text-sm)" }}>
                      {option.label}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {value.length > 0 && (
            <div className="border-t border-duvi-border p-2">
              <button
                onClick={() => onChange([])}
                className="w-full flex items-center justify-center gap-1 text-duvi-text-secondary hover:text-duvi-text-primary transition-colors py-1"
                style={{ fontSize: "var(--text-2xs)" }}
              >
                <X className="w-3 h-3" />
                Clear all
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
